import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import FormInput from '@/shared/components/forms/FormInput';
import FormSelect from '@/shared/components/forms/FormSelect';
import FormTextArea from '@/shared/components/forms/FormTextArea';

interface IOrganization {
  id: number;
  organizationName: string;
  companyName: string;
  industry: string;
  email: string;
  adminName?: string;
  users: number;
  projects_count: number;
  test_cases_count: number;
  status: 'Active' | 'Inactive';
  created: string;
  description?: string;
  country?: string;
}

interface IEditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (organization: IOrganization) => void;
  organization: IOrganization | null;
}

interface IEditOrgForm {
  organizationName: string;
  companyName: string;
  industry: string;
  description: string;
  country: string;
}

const INDUSTRY_OPTIONS = [
  { value: 'Technology', label: 'Technology' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Education', label: 'Education' },
  { value: 'Retail', label: 'Retail' },
  { value: 'E-commerce', label: 'E-commerce' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Media & Entertainment', label: 'Media & Entertainment' },
  { value: 'Other', label: 'Other' },
];

const COUNTRY_OPTIONS = [
  { value: 'Afghanistan', label: 'Afghanistan' },
  { value: 'Albania', label: 'Albania' },
  { value: 'Algeria', label: 'Algeria' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Bangladesh', label: 'Bangladesh' },
  { value: 'Belgium', label: 'Belgium' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Chile', label: 'Chile' },
  { value: 'China', label: 'China' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'Czech Republic', label: 'Czech Republic' },
  { value: 'Denmark', label: 'Denmark' },
  { value: 'Egypt', label: 'Egypt' },
  { value: 'Ethiopia', label: 'Ethiopia' },
  { value: 'Finland', label: 'Finland' },
  { value: 'France', label: 'France' },
  { value: 'Germany', label: 'Germany' },
  { value: 'Ghana', label: 'Ghana' },
  { value: 'Greece', label: 'Greece' },
  { value: 'India', label: 'India' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Iran', label: 'Iran' },
  { value: 'Iraq', label: 'Iraq' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Israel', label: 'Israel' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Jordan', label: 'Jordan' },
  { value: 'Kenya', label: 'Kenya' },
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Morocco', label: 'Morocco' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'New Zealand', label: 'New Zealand' },
  { value: 'Nigeria', label: 'Nigeria' },
  { value: 'Norway', label: 'Norway' },
  { value: 'Pakistan', label: 'Pakistan' },
  { value: 'Peru', label: 'Peru' },
  { value: 'Philippines', label: 'Philippines' },
  { value: 'Poland', label: 'Poland' },
  { value: 'Portugal', label: 'Portugal' },
  { value: 'Romania', label: 'Romania' },
  { value: 'Russia', label: 'Russia' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'South Korea', label: 'South Korea' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Sri Lanka', label: 'Sri Lanka' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Thailand', label: 'Thailand' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'Ukraine', label: 'Ukraine' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'United States', label: 'United States' },
  { value: 'Venezuela', label: 'Venezuela' },
  { value: 'Vietnam', label: 'Vietnam' },
];

const compactField = '!rounded-md !py-2 !text-sm border-gray-300';
const labelCompact = 'text-sm font-medium text-gray-700 mb-2';

const EditOrganizationModal = ({
  isOpen,
  onClose,
  onUpdate,
  organization,
}: IEditOrganizationModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IEditOrgForm>({
    defaultValues: {
      organizationName: '',
      companyName: '',
      industry: '',
      description: '',
      country: '',
    },
  });

  const industryValue = watch('industry');
  const countryValue = watch('country');

  useEffect(() => {
    if (!organization) {
      return;
    }
    const normalizeValue = (value: string | undefined): string => {
      if (!value || value === 'N/A' || value.trim() === 'N/A') {
        return '';
      }
      return value;
    };

    reset({
      organizationName: organization.organizationName || '',
      companyName: organization.companyName || '',
      industry: normalizeValue(organization.industry),
      description: organization.description || '',
      country: organization.country || '',
    });
  }, [organization, reset]);

  if (!isOpen || !organization) {
    return null;
  }

  const onValid = async (data: IEditOrgForm) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const updatedOrganization: IOrganization = { ...organization, ...data };
      onUpdate(updatedOrganization);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => onClose();

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5'
      onClick={handleClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      }}
      role='button'
      tabIndex={0}
      aria-label='Close modal'
    >
      <div
        className='bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role='dialog'
        tabIndex={-1}
      >
        <div className='flex justify-between items-center p-6 border-b border-gray-200'>
          <h2 className='text-xl font-semibold text-gray-900 m-0'>Edit Organization</h2>
          <button
            type='button'
            className='bg-transparent border-none cursor-pointer text-gray-500 p-1 flex items-center justify-center rounded hover:text-gray-900 hover:bg-gray-100'
            onClick={handleClose}
            aria-label='Close'
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M15 5L5 15M5 5L15 15'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onValid)} className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
            <div className='flex flex-col gap-5'>
              <FormInput
                name='organizationName'
                label='Org Name'
                labelClassName={labelCompact}
                placeholder='Enter organization name'
                register={register}
                errors={errors}
                validation={{ required: 'Organization name is required' }}
                className={compactField}
              />

              <FormSelect
                name='industry'
                label='Industry'
                placeholder='Select industry'
                options={INDUSTRY_OPTIONS}
                register={register}
                errors={errors}
                value={industryValue}
                onChange={(val) => setValue('industry', val as string, { shouldValidate: true })}
                validation={{ required: 'Industry is required' }}
                containerClassName='[&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-700'
                className={`${compactField} border-gray-300!`}
              />
            </div>

            <div className='flex flex-col gap-5'>
              <FormInput
                name='companyName'
                label='Company Name'
                labelClassName={labelCompact}
                placeholder='Enter company name (optional)'
                register={register}
                errors={errors}
                className={compactField}
              />

              <FormSelect
                name='country'
                label='Country'
                placeholder='Select country'
                options={COUNTRY_OPTIONS}
                register={register}
                errors={errors}
                value={countryValue}
                onChange={(val) => setValue('country', val as string, { shouldValidate: true })}
                searchable
                containerClassName='[&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-700'
                className={`${compactField} border-gray-300!`}
              />
            </div>
          </div>

          <FormTextArea
            name='description'
            label='Description'
            placeholder='Brief description of the organization'
            register={register}
            errors={errors}
            rows={4}
            resize='vertical'
            showRequiredMark={false}
            className={`${compactField} min-h-[100px]`}
            containerClassName='mb-6 [&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-700'
          />

          <div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
            <button
              type='button'
              className='px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 cursor-pointer'
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-4 py-2 bg-theme-500 text-white rounded-md text-sm font-semibold hover:bg-theme-600 disabled:opacity-50 cursor-pointer border-none'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOrganizationModal;
